'use client';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { FaStar } from 'react-icons/fa';

import type {
  ProductDetailData,
  ProductReview,
} from '@eliadmin/core/src/api/products/types/product-types';

import { AddReviewDialog } from '@eliadmin/core/src/components/product/dialog/add-review-dialog';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { ProductRating } from '@eliadmin/core/src/components/product/detail/product-rating';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';

import { Button } from '../../ui/button';
import { useQuery } from '@tanstack/react-query';
import { fetchProductReviews } from '@eliadmin/core/src/api/products/product-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { FaPencil } from 'react-icons/fa6';
import { HtmlDescription } from '@eliadmin/core/src/components/ui/html-description';
import { Heading } from '../../ui/heading';
import { Modal } from '../../ui/modal';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import Link from '@eliadmin/core/src/components/link';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { Paragraph } from '../../ui/paragraph';

export function ProductTabsReviews({
  product,
  locale,
}: {
  product: ProductDetailData;
  locale: string;
}) {
  const { t } = useTranslations();
  const openDialog = useDialogStore(s => s.open);

  const me = useCustomerStore(s => s.me);
  const inicialized = useCustomerStore(s => s.isInitialized);

  const isLogged = inicialized && !!me;

  const { data: reviews, isLoading } = useQuery({
    queryKey: [CacheKey.PRODUCTS_REVIEWS, locale, product.slug],
    queryFn: () => fetchProductReviews({ locale, slug: product.slug }),
  });

  const handleCreateReviewClick = () => {
    openDialog({
      id: AddReviewDialog.id,
      content: <AddReviewDialog slug={product.slug} />,
    });
  };

  const averageRating = reviews?.data.length
    ? reviews.data.reduce((sum, review) => sum + review.rating, 0) / reviews.data.length
    : product.rating || 0;
  const ratingPercentage = Math.round((averageRating / 5) * 100);

  const getReviewCountText = (count: number) => {
    if (count === 1) return `${count} hodnotenie`;
    if (count >= 2 && count <= 4) return `${count} hodnotenia`;
    return `${count} hodnotení`;
  };

  return (
    <section className={cn('custom-section', 'gap-6 px-0 py-8 sm:py-10 lg:py-12')}>
      <div
        className={cn(
          'custom-container',
          'flex w-full flex-col items-start justify-start sm:flex-row sm:items-center sm:justify-between',
        )}
      >
        <Heading tag="h5" title={t('text447')} className={{ title: 'uppercase' }} />
      </div>
      <div
        className={cn(
          'custom-container',
          'flex w-full flex-col items-start justify-start gap-12 md:flex-row md:justify-between md:gap-6 lg:gap-12',
        )}
      >
        <div className="flex w-full flex-col items-start justify-start gap-6 md:max-w-[604px]">
          <div className="flex w-full items-start justify-start gap-6 md:max-w-[604px]">
            <div className="flex flex-col items-start justify-start gap-2">
              <p className="text-5xl font-semibold text-black sm:text-7xl md:text-8xl lg:text-[140px] xl:text-[160px]">
                {product?.rating ? parseFloat(product?.rating?.toString())?.toFixed(1) : '5.0'}
                <span className="text-lg font-semibold text-[#666464] sm:text-xl lg:text-2xl">
                  /5
                </span>
              </p>
              <p className="whitespace-nowrap text-sm font-medium text-muted sm:text-base md:text-lg lg:text-xl xl:text-2xl">
                ({getReviewCountText(reviews?.data.length ?? 0)})
              </p>
            </div>
            <div className="flex w-full max-w-[317px] flex-col items-start justify-start gap-2">
              {[5, 4, 3, 2, 1].map(starRating => {
                const ratingCount =
                  reviews?.data.filter(review => review.rating === starRating).length || 0;
                const barWidth = ratingCount > 0 ? Math.min(ratingCount * 20, 100) : 1;

                return (
                  <div key={starRating} className="flex w-full items-center justify-start gap-1">
                    <FaStar size={18} className="min-h-[18px] min-w-[18px] text-[#EDB000]" />
                    <p className=" whitespace-nowrap text-lg text-foreground sm:text-xl lg:text-2xl">
                      {starRating}
                    </p>
                    <div className="mx-1 flex h-[6px] w-full min-w-2 items-center justify-start rounded-full bg-[#EBEBEB]">
                      <div
                        className="h-[6px] rounded-full bg-foreground"
                        style={{ width: `${barWidth}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {isLogged ? (
            <Modal
              trigger={
                <Button className="w-full gap-3 sm:w-fit">
                  <FaPencil size={20} /> {t('text3397')}
                </Button>
              }
              className={{
                content:
                  'flex max-h-[90dvh] w-full max-w-[744px] flex-col items-start justify-start gap-6 overflow-hidden rounded-[4px] bg-white px-4 py-6 sm:max-w-[744px] sm:px-6',
              }}
            >
              <AddReviewDialog slug={product.slug} />
            </Modal>
          ) : (
            <Button asChild className="w-full gap-3 sm:w-fit">
              <Link prefetch={false} href={ROUTES.SIGN_IN()}>
                <FaPencil size={20} /> {t('text3397')}
              </Link>
            </Button>
          )}
        </div>

        {isLoading && <Spinner className={cn('', 'ea-product-detail-tab-reviews__spinner')} />}
        {reviews?.data.length ? (
          <div className="flex w-full max-w-[604px] flex-col items-start justify-start gap-4">
            {reviews.data.map(review => (
              <ProductReviewCard key={review.slug} review={review} />
            ))}
          </div>
        ) : (
          !isLoading && (
            <div
              className={cn(
                'mt-4 w-full text-center text-lg font-semibold text-muted-foreground',
                'ea-product-detail-tab-reviews__no-reviews-text',
              )}
            >
              {t('text608')}
            </div>
          )
        )}
      </div>
    </section>
  );
}

function ProductReviewCard({ review }: { review: ProductReview }) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1)
      .toString()
      .padStart(2, '0')}.${date.getFullYear()}`;
  };

  return (
    <div className="flex w-full flex-col items-start justify-start gap-4 rounded-[8px] border border-border p-4 sm:p-5 lg:p-6">
      <div className="flex w-full items-start justify-between gap-2">
        <div className="flex flex-col items-start justify-start gap-2">
          <Heading title={`${review.name} ${review.surname}`} tag="h6" />
          <ProductRating
            rating={review.rating}
            className={{
              main: 'flex items-center justify-start gap-2',
              starIcon: 'text-[#EDB000]',
              starDisabled: 'text-[#EBEBEB]',
            }}
          />
        </div>
        <span className="text-sm font-normal text-muted lg:text-base">
          {formatDate(review.created_at)}
        </span>
      </div>
      <Paragraph
        innerHtml={review.review}
        color="muted"
        size="lg"
        className={cn('w-full text-left', 'ea-product-reviews__description')}
      />
    </div>
  );
}
